/* OfflineDiscoveryDownloadAction.js */

"use strict";

const _ = require("lodash");

/**
 * Class representing an action to download the offline discovery bundle for IoT devices.
 *
 * OfflineDiscoveryDownloadAction is responsible for:
 *  - Initiating the download process for the offline discovery bundle.
 *  - Providing user feedback overlays during the operation.
 *  - Saving the retrieved bundle as a zip file.
 *  - Handling errors and displaying appropriate user messages.
 *
 * Usage:
 *   const action = new OfflineDiscoveryDownloadAction(logger, view, userSession, userFeedback, steveIo, OdmPrefsEntity);
 *   action.run();
 */
class OfflineDiscoveryDownloadAction {
    /**
     * Create an OfflineDiscoveryDownloadAction.
     * @param {Object} logger - Logger instance.
     * @param {Object} activeView - Current active view (UI context).
     * @param {Object} userSession - Current user session.
     * @param {Object} userFeedback - Modal user feedback handler.
     * @param {Object} steveIo - Steve IO utilities.
     * @param {Object} OdmPrefsEntity - Entity for offline discovery management preferences.
     */
    constructor(logger, activeView, userSession, userFeedback, steveIo, OdmPrefsEntity) {
        this._logger = logger;
        this._activeView = activeView;
        this._modalUserFeedback = userFeedback;
        this._OdmPrefsEntity = OdmPrefsEntity;

        this._fileFactory = steveIo.fileFactory;

        this._userSession = userSession;
        this._activeOrgId = _.get(this._userSession, "activeOrganization.id");

        this.run = this.run.bind(this);
    }

    /**
     * Initiates the offline discovery bundle download workflow.
     * Shows progress overlays, retrieves the bundle, and saves it as a zip file.
     * Handles errors and provides user feedback.
     * @returns {Promise<void>}
     */
    async run() {
        // Show progress veil
        await this._modalUserFeedback.displayLoading({
            message: "Retrieving offline discovery bundle...",
        });

        try {
            const bundleResults = await this._OdmPrefsEntity.runDiscoveryBundleWork(
                this._activeOrgId,
                {}
            );
            const summary = bundleResults && bundleResults.summary;

            if (summary && summary.success && summary.bundle) {
                const zipBuffer = Buffer.from(summary.bundle, "base64");
                await this._fileFactory.saveArrayBufferAsFile(zipBuffer, "OfflineDiscovery.zip");
                await this._modalUserFeedback.hideDisplayed();
            } else {
                this._logger.error(
                    "Failed to retrieve offline discovery bundle: ",
                    summary && summary.errorMsg
                );
                this._modalUserFeedback.displayError({
                    errorMsg:
                        "Unable to retrieve offline discovery bundle. Please try again later.",
                });
            }
        } catch (err) {
            this._logger.error("Failed to retrieve offline discovery bundle: ", err);
            this._modalUserFeedback.displayError({
                errorMsg: "Unable to retrieve offline discovery bundle. Please try again later.",
            });
        }
    }

    /**
     * Cleans up any resources and hides UI overlays.
     */
    shutdown() {
        this._logger = undefined;
        this._activeView = undefined;
        this._modalUserFeedback = undefined;
        this._OdmPrefsEntity = undefined;
        this._fileFactory = undefined;
        this._userSession = undefined;
        this._activeOrgId = undefined;
    }
}

module.exports = OfflineDiscoveryDownloadAction;

OfflineDiscoveryDownloadAction.$inject = [
    "$teve.logger",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.io",
    "$teve.entities.OdmPrefs",
];

OfflineDiscoveryDownloadAction.$displayDetails = {
    type: "Action",
    name: "OfflineDiscoveryDownloadAction",
    icon: "devicemanager.offline_discovery_download",
    shown: true,
    title: "Discover offline devices",
    order: 1,
};
